/* eslint-disable no-console */
/**
 * P2 — view-only → canonical namespace relabel (railgun-spend-engine-warm-plan.md §5).
 *
 * P1 proved many engines can write ONE shared `zkx-railgun-db-sw` safely. But the
 * workers scan under their deterministic VIEW-ONLY wallet id
 * (`generateID(shareableViewingKey)`), so the spend engine's CANONICAL wallet id
 * has no notes under it — gas estimation still sees 0 spendable.
 *
 * Fix without re-decrypting (§4 doc): the small, wallet-scoped namespace is cheap
 * to copy. After a worker finishes a chain, copy every key under
 * `wallet/<viewOnlyId>/<chain>/*` to `wallet/<canonicalId>/<chain>/*`:
 *   - received commitments  (`wallet/<id>/<chain>/<tree>/<pos>`)
 *   - wallet details         (`wallet/<id>/<chain>/details`)
 *   - merkletree history ver (`wallet/<id>/<chain>/merkleetree_history_version/<chainID>`)
 *   - spent commitments      (`wallet/<id>-spent/<chain>/<tree>/<pos>`)
 * The merkletree (chain-scoped, large) is NOT copied — both engines read it from
 * the shared DB. POI state rides inside each commitment record, so it copies along.
 *
 * Nullifiers derive from the viewing key, identical between the view-only and the
 * full wallet, so notes the view-only decrypted are spendable by the full wallet
 * (§4 doc) — the relabel is sound, not a hack.
 *
 * Gated by RAILGUN_SHARED_SCAN_DB; no-op on the production path.
 */
import type { Chain } from '@railgun-community/shared-models'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import { RAILGUN_SHARED_SCAN_DB } from '@web/utils/railgunCore'

const LOG = '[Relabel]'

/** Minimal shape of the engine wallet methods we touch. Both full + view-only have them. */
type WalletLike = {
  id: string
  db: {
    getNamespaceKeys: (namespace: string[]) => Promise<string[]>
    get: (path: string[], encoding?: string) => Promise<unknown>
    put: (path: string[], value: unknown, encoding?: string) => Promise<void>
  }
  getWalletDBPrefix: (chain: Chain) => string[]
  getWalletSentCommitmentDBPrefix: (chain: Chain) => string[]
  getUTXOMerkletreeHistoryVersionDBPrefix: (chain: Chain) => string[]
  // Drops the in-RAM receive/sent commitment cache for a chain so the next
  // balance read re-queries the DB. Present on AbstractWallet (both full + view-only).
  invalidateCommitmentsCache: (chain: Chain) => void
}

// canonicalWalletId -> deterministic view-only id, so we load it once per wallet.
const viewOnlyIdByCanonical = new Map<string, string>()

async function ensureViewOnlyLoaded(
  canonicalWalletId: string,
  encryptionKey: string,
  shareableViewingKey: string
): Promise<WalletLike> {
  const { createViewOnlyRailgunWallet, viewOnlyWalletForID } = await loadRailgunSdk()
  const cached = viewOnlyIdByCanonical.get(canonicalWalletId)
  if (cached) {
    try {
      return viewOnlyWalletForID(cached) as unknown as WalletLike
    } catch {
      // Engine was reset / wallet unloaded — recreate below.
    }
  }
  const info = await createViewOnlyRailgunWallet(encryptionKey, shareableViewingKey, undefined)
  viewOnlyIdByCanonical.set(canonicalWalletId, info.id)
  return viewOnlyWalletForID(info.id) as unknown as WalletLike
}

/**
 * Copy every stored key under `viewPrefix` to `canonPrefix`. The two prefixes are
 * the SAME path except the wallet-id segment (index 1), so a key's trailing
 * segments are preserved verbatim. Read+write with the same per-key encoding makes
 * each copy a faithful byte copy (stored bytes round-trip).
 */
async function copyNamespace(
  db: WalletLike['db'],
  viewPrefix: string[],
  canonPrefix: string[],
  encodingFor: (segments: string[]) => string
): Promise<number> {
  const keys = await db.getNamespaceKeys(viewPrefix)
  let copied = 0
  for (const key of keys) {
    const segments = key.split(':')
    const encoding = encodingFor(segments)
    // eslint-disable-next-line no-await-in-loop
    const value = await db.get(segments, encoding)
    const newPath = [...canonPrefix, ...segments.slice(canonPrefix.length)]
    // eslint-disable-next-line no-await-in-loop
    await db.put(newPath, value, encoding)
    copied += 1
  }
  return copied
}

/**
 * Relabel one chain's wallet namespace from the worker's view-only id to the
 * spend engine's canonical id, in the shared DB.
 */
export async function relabelChainAfterWorkerScan(
  chain: Chain,
  canonicalWalletId: string,
  encryptionKey: string,
  shareableViewingKey: string
): Promise<{ received: number; spent: number }> {
  if (!RAILGUN_SHARED_SCAN_DB) return { received: 0, spent: 0 }

  const { walletForID } = await loadRailgunSdk()
  const viewOnly = await ensureViewOnlyLoaded(canonicalWalletId, encryptionKey, shareableViewingKey)
  const canonical = walletForID(canonicalWalletId) as unknown as WalletLike
  const { db } = canonical

  // Under the id namespace live: received commitments + details (hex) and the
  // merkletree history-version (stored utf8). Detect the latter by its literal
  // 4th segment so we copy it with the right encoding.
  const historyVersionSegment = viewOnly.getUTXOMerkletreeHistoryVersionDBPrefix(chain)[3]?.toLowerCase()
  const idNamespaceEncoding = (segments: string[]): string =>
    segments.length === 5 &&
    historyVersionSegment != null &&
    segments[3]?.toLowerCase() === historyVersionSegment
      ? 'utf8'
      : 'hex'

  const received = await copyNamespace(
    db,
    viewOnly.getWalletDBPrefix(chain),
    canonical.getWalletDBPrefix(chain),
    idNamespaceEncoding
  )
  const spent = await copyNamespace(
    db,
    viewOnly.getWalletSentCommitmentDBPrefix(chain),
    canonical.getWalletSentCommitmentDBPrefix(chain),
    () => 'hex'
  )

  // The copy wrote new commitment records (incl. updated POI state) straight into
  // the canonical wallet's DB namespace, behind the live canonical wallet object's
  // back — it never scanned this chain, so its in-RAM commitment cache is stale and
  // `getTokenBalances` would keep returning the pre-copy view (0 spendable, or notes
  // stuck "pending POI") until a popup reload rebuilt the wallet. Drop the cache so
  // the next balance/estimate re-reads the fresh DB — the scoped equivalent of that
  // reload. See railgun-spend-engine-warm-plan.md §P3-fix.
  canonical.invalidateCommitmentsCache(chain)

  console.log(`${LOG} chain ${chain.id} relabeled view-only → canonical`, { received, spent })
  return { received, spent }
}
